// LUCKY DUCKY · PHASE 2 CLOSED · BETO RESPINS state boundary
// Source of truth for LOCKED rules: spec/beto_respins_state_boundary.md
// Source of truth for PRODUCTION_SPEC:  spec/LUCKY_DUCKY_PRODUCTION_SPEC.md
// Source of truth for reference matrix: spec/V11_EVIDENCE_RESOLUTION.md
//
// LOCKED (Cardinal-verified — CANNOT be overridden by PRODUCTION_SPEC):
//   · Feature name: BETO RESPINS.
//   · 2+ BONUS enters the respin path.
//   · 3+ qualifying BONUS awards 15 free plays.
//   · Feature non-scatter wins pay 3×.
//
// PRODUCTION_SPEC decisions (labelled — NOT verified reference behaviour):
//   · Retrigger: 3+ BONUS during a free play → +10 free plays. (PROD-10)
//   · Retrigger stacks recursively.                            (PROD-11)
//   · No BONUS hold/lock during respin.                        (PROD-1)
//   · One respin per 2-BONUS trigger; no counter reset.        (PROD-2..PROD-4)
//   · Mixed 3+ Wild + standard line = 0 pay + telemetry flag.  (PROD-15)
//
// UNVERIFIED_REFERENCE_BEHAVIOR flags (not blocking production):
//   All P0/P1/P2/P3 rows in spec/V11_EVIDENCE_RESOLUTION.md.
import { RULES } from './paytable';

export const STATE = Object.freeze({
  IDLE:              'IDLE',           // Awaiting player action
  SPINNING:          'SPINNING',       // Base-game reels rolling
  BASE_RESULT:       'BASE_RESULT',    // Evaluated normal spin, no bonus path
  RESPIN_PENDING:    'RESPIN_PENDING', // 2+ BONUS landed → about to respin
  RESPIN_ACTIVE:     'RESPIN_ACTIVE',  // Respin executing (transitions NEEDS_VERIFICATION)
  FEATURE_INTRO:     'FEATURE_INTRO',  // Show LD-14 overlay, awaiting continue
  FEATURE_ACTIVE:    'FEATURE_ACTIVE', // Inside 15-free-play BETO RESPINS
  FEATURE_COMPLETE:  'FEATURE_COMPLETE'
});

// Feature flags. Each value is a PRODUCTION_SPEC decision (see
// spec/LUCKY_DUCKY_PRODUCTION_SPEC.md). None of these values are a claim
// about the physical reference machine. Reference-machine behavior for
// every one of these is UNVERIFIED_REFERENCE_BEHAVIOR (see
// spec/V11_EVIDENCE_RESOLUTION.md).
export const FEATURE_FLAGS = {
  respin_hold_and_lock:         false,   // PROD-1  — no positional hold; all 15 positions respin
  respin_accumulate_landings:   false,   // PROD-6  — re-eval on respun grid, no cross-grid accumulation
  retrigger_plus10_free_plays:  true,    // PROD-10 — 3+ BONUS on a free-play grid → +10 free plays
  retrigger_can_stack:          true,    // PROD-11 — retriggered plays can themselves retrigger
  highest_paying_wild_final:    false,   // PROD-16
  max_play_bonus_odds_boost:    false,   // PROD-17
};

/**
 * Decide the NEXT state after a spin evaluation.
 *  · 3+ BONUS  → FEATURE_INTRO
 *  · 2  BONUS  → RESPIN_PENDING  (respin transitions are NEEDS_VERIFICATION; we
 *                just enter a "single respin, no holds" default until the real
 *                mechanic is supplied)
 *  · else      → BASE_RESULT
 * Inside a running feature, returns FEATURE_ACTIVE / FEATURE_COMPLETE.
 */
export function nextState({ prev, evaluation, freePlaysLeft }) {
  if (prev === STATE.FEATURE_ACTIVE) {
    // Inside a free play — decrement, and NEEDS_VERIFICATION for retrigger.
    if (FEATURE_FLAGS.retrigger_plus10_free_plays && evaluation.enters_free_plays) {
      // Skeleton branch — Cardinal supplies exact rule.
      return { state: STATE.FEATURE_ACTIVE, freePlaysLeft: freePlaysLeft - 1 + 10 };
    }
    const remaining = freePlaysLeft - 1;
    return { state: remaining > 0 ? STATE.FEATURE_ACTIVE : STATE.FEATURE_COMPLETE, freePlaysLeft: remaining };
  }
  if (evaluation.enters_free_plays) {
    return { state: STATE.FEATURE_INTRO, freePlaysLeft: RULES.free_plays_awarded_on_trigger };
  }
  if (evaluation.enters_respin) {
    return { state: STATE.RESPIN_PENDING, freePlaysLeft: 0 };
  }
  return { state: STATE.BASE_RESULT, freePlaysLeft: 0 };
}

/**
 * Perform a respin.  DEFAULT (until Cardinal supplies real transitions):
 *   · No holds. All reels respin fresh.
 *   · 1 respin attempt only.
 *   · If ≥3 BONUS lands during the respin → enters feature.
 *   · Else → return to BASE_RESULT.
 * When Cardinal supplies hold/lock rules, replace this function's body
 * without touching downstream callers.
 */
export function defaultRespin(spinFn, evalFn) {
  const grid = spinFn();
  const evaluation = evalFn(grid, /*isFeature=*/false);
  return { grid, evaluation, respin_note: 'default-single-respin-no-holds (NEEDS_VERIFICATION)' };
}
